import { Media, MediaImage, MediaUseCase } from '../types/media';

/**
 * Media utility functions for handling Media and MediaImage objects
 * Integrates with existing media URL system and provides face manipulation capabilities
 *
 * Face format: [x1, y1, x2, y2] where:
 * - x1, y1: top-left coordinates
 * - x2, y2: bottom-right coordinates
 */

/**
 * Validates if a Media object has valid structure
 */
export const isValidMedia = (media: Media): boolean => {
    return (
        media &&
        typeof media === 'object' &&
        Array.isArray(media.images) &&
        typeof media.mediaId === 'number' &&
        media.images.every(isValidMediaImage)
    );
};

/**
 * Validates if a MediaImage object has valid structure
 */
export const isValidMediaImage = (image: MediaImage): boolean => {
    return (
        image &&
        typeof image === 'object' &&
        typeof image.url === 'string' &&
        typeof image.width === 'number' &&
        typeof image.height === 'number' &&
        image.width > 0 &&
        image.height > 0
    );
};

/**
 * Gets the primary image from a Media object (first image in the array)
 */
export const getPrimaryImage = (media: Media): MediaImage | null => {
    return media.images.length > 0 ? media.images[0] : null;
};

/**
 * Gets all images from a Media object
 */
export const getAllImages = (media: Media): MediaImage[] => {
    return media.images || [];
};

/**
 * Gets the largest image by area (width * height)
 */
export const getLargestImage = (media: Media): MediaImage | null => {
    if (media?.images?.length === 0) return null;

    return media?.images?.reduce((largest, current) => {
        const largestArea = largest.width * largest.height;
        const currentArea = current.width * current.height;
        return currentArea > largestArea ? current : largest;
    });
};

/**
 * Gets the smallest image by area (width * height)
 */
export const getSmallestImage = (media: Media): MediaImage | null => {
    if (media?.images?.length === 0) return null;

    return media?.images?.reduce((smallest, current) => {
        const smallestArea = smallest.width * smallest.height;
        const currentArea = current.width * current.height;
        return currentArea < smallestArea ? current : smallest;
    });
};

/**
 * Calculates the aspect ratio of an image
 */
export const getImageAspectRatio = (image: MediaImage): number => {
    return image.width / image.height;
};

/**
 * Checks if an image is landscape (width > height)
 */
export const isLandscape = (image: MediaImage): boolean => {
    return image.width > image.height;
};

/**
 * Checks if an image is portrait (height > width)
 */
export const isPortrait = (image: MediaImage): boolean => {
    return image.height > image.width;
};

/**
 * Checks if an image is square (width === height)
 */
export const isSquare = (image: MediaImage): boolean => {
    return image.width === image.height;
};

/**
 * Gets images filtered by orientation
 */
export const getImagesByOrientation = (
    media: Media,
    orientation: 'landscape' | 'portrait' | 'square'
): MediaImage[] => {
    return media?.images?.filter(image => {
        switch (orientation) {
            case 'landscape':
                return isLandscape(image);
            case 'portrait':
                return isPortrait(image);
            case 'square':
                return isSquare(image);
            default:
                return false;
        }
    });
};

/**
 * Checks if a Media object has face detection data
 */
export const hasFaceDetection = (media: Media): boolean => {
    return Boolean(media && media.faces && media.faces.length > 0);
};

/**
 * Reduces multiple face boxes to a single bounding box
 * Returns [min(x1), min(y1), max(x2), max(y2)] that encompasses all faces
 */
export const getBoundingBoxFromFaces = (
    faces: [number, number, number, number][]
): [number, number, number, number] | null => {
    if (!faces || faces.length === 0) return null;

    let minX1 = Infinity;
    let minY1 = Infinity;
    let maxX2 = -Infinity;
    let maxY2 = -Infinity;

    faces.forEach(face => {
        const [x1, y1, x2, y2] = face;
        minX1 = Math.min(minX1, x1);
        minY1 = Math.min(minY1, y1);
        maxX2 = Math.max(maxX2, x2);
        maxY2 = Math.max(maxY2, y2);
    });

    return [minX1, minY1, maxX2, maxY2];
};

export const sortImages = (
    images: MediaImage[],
    criteria: 'area' | 'width' | 'height' | 'aspectRatio' = 'area',
    order: 'asc' | 'desc' = 'asc'
): MediaImage[] => {
    return [...images].sort((a, b) => {
        let aValue: number;
        let bValue: number;

        switch (criteria) {
            case 'width':
                aValue = a.width;
                bValue = b.width;
                break;
            case 'height':
                aValue = a.height;
                bValue = b.height;
                break;
            case 'area':
                aValue = a.width * a.height;
                bValue = b.width * b.height;
                break;
            case 'aspectRatio':
                aValue = a.width / a.height;
                bValue = b.width / b.height;
                break;
            default:
                return 0;
        }

        return order === 'asc' ? aValue - bValue : bValue - aValue;
    });
};

/**
 * Gets the unified bounding box for all faces in media
 */
export const getUnifiedFaceBox = (media: Media): [number, number, number, number] | null => {
    if (!media?.faces || media?.faces?.length === 0) return null;
    return getBoundingBoxFromFaces(media.faces);
};

/**
 * Calculates face area (width * height) from face coordinates [x1, y1, x2, y2]
 */
export const getFaceArea = (face: [number, number, number, number]): number => {
    const [x1, y1, x2, y2] = face;
    const width = x2 - x1;
    const height = y2 - y1;
    return width * height;
};

/**
 * Gets the largest face by area
 */
export const getLargestFace = (media: Media): [number, number, number, number] | null => {
    if (!media?.faces || media?.faces?.length === 0) return null;

    return media.faces.reduce((largest, current) => {
        const largestArea = getFaceArea(largest);
        const currentArea = getFaceArea(current);
        return currentArea > largestArea ? current : largest;
    });
};

/**
 * Gets the smallest face by area
 */
export const getSmallestFace = (media: Media): [number, number, number, number] | null => {
    if (!media?.faces || media?.faces?.length === 0) return null;

    return media.faces.reduce((smallest, current) => {
        const smallestArea = getFaceArea(smallest);
        const currentArea = getFaceArea(current);
        return currentArea < smallestArea ? current : smallest;
    });
};

/**
 * Checks if a face is in the center region of the image
 */
export const isFaceCentered = (
    face: [number, number, number, number],
    imageWidth: number,
    imageHeight: number,
    centerThreshold: number = 0.3
): boolean => {
    const [x1, y1, x2, y2] = face;
    const centerX = (x1 + x2) / 2;
    const centerY = (y1 + y2) / 2;

    const imageCenterX = imageWidth / 2;
    const imageCenterY = imageHeight / 2;

    const xOffset = Math.abs(centerX - imageCenterX) / imageWidth;
    const yOffset = Math.abs(centerY - imageCenterY) / imageHeight;

    return xOffset <= centerThreshold && yOffset <= centerThreshold;
};

/**
 * Gets faces that are centered in the image
 */
export const getCenteredFaces = (
    media: Media,
    imageWidth: number,
    imageHeight: number,
    centerThreshold: number = 0.3
): [number, number, number, number][] => {
    if (!media?.faces) return [];

    return media.faces.filter(face =>
        isFaceCentered(face, imageWidth, imageHeight, centerThreshold)
    );
};

/**
 * Centers a face box to the center of the display image
 * Returns the centered coordinates [x1, y1, x2, y2]
 */
export const centerFaceBox = (
    face: [number, number, number, number],
    imageWidth: number,
    imageHeight: number
): [number, number, number, number] => {
    const [x1, y1, x2, y2] = face;
    const faceWidth = x2 - x1;
    const faceHeight = y2 - y1;

    const imageCenterX = imageWidth / 2;
    const imageCenterY = imageHeight / 2;

    const newX1 = imageCenterX - faceWidth / 2;
    const newY1 = imageCenterY - faceHeight / 2;
    const newX2 = imageCenterX + faceWidth / 2;
    const newY2 = imageCenterY + faceHeight / 2;

    return [newX1, newY1, newX2, newY2];
};

/**
 * Centers the unified face box to the center of the display image
 */
export const centerUnifiedFaceBox = (
    media: Media,
    imageWidth: number,
    imageHeight: number
): [number, number, number, number] | null => {
    const unifiedBox = getUnifiedFaceBox(media);
    if (!unifiedBox) return null;

    return centerFaceBox(unifiedBox, imageWidth, imageHeight);
};

/**
 * Gets the best image for a specific use case (e.g., thumbnail, hero, etc.)
 */
export const getBestImageForUse = (media: Media, useCase: MediaUseCase): MediaImage | null => {
    if (!media || media?.images?.length === 0) return null;

    switch (useCase) {
        case 'thumbnail':
            const squareImages = getImagesByOrientation(media, 'square');
            if (squareImages.length > 0) {
                return getSmallestImage({ ...media, images: squareImages });
            }
            return getSmallestImage(media);

        case 'hero':
            const landscapeImages = getImagesByOrientation(media, 'landscape');
            if (landscapeImages.length > 0) {
                return getLargestImage({ ...media, images: landscapeImages });
            }
            return getLargestImage(media);

        case 'gallery':
            return getLargestImage(media);

        case 'social':
            const socialImages = [
                ...getImagesByOrientation(media, 'square'),
                ...getImagesByOrientation(media, 'landscape')
            ];
            if (socialImages.length > 0) {
                return getLargestImage({ ...media, images: socialImages });
            }
            return getLargestImage(media);

        case 'print':
            return getLargestImage(media);

        default:
            return getPrimaryImage(media);
    }
};

/**
 * Calculates the optimal crop area based on face detection
 */
export const getOptimalCropArea = (
    media: Media,
    targetWidth: number,
    targetHeight: number,
    imageIndex: number = 0
): { x: number; y: number; width: number; height: number } | null => {
    if (imageIndex >= media?.images?.length) return null;

    const image = media.images[imageIndex];

    // If no faces detected, center crop
    if (!hasFaceDetection(media)) {
        return getCenterCropArea(image, targetWidth, targetHeight);
    }

    // Get the unified face box for face-aware cropping
    const unifiedFaceBox = getUnifiedFaceBox(media);
    if (!unifiedFaceBox) {
        return getCenterCropArea(image, targetWidth, targetHeight);
    }

    return getFaceAwareCropArea(image, unifiedFaceBox, targetWidth, targetHeight);
};

/**
 * Gets center crop area for an image
 */
const getCenterCropArea = (
    image: MediaImage,
    targetWidth: number,
    targetHeight: number
): { x: number; y: number; width: number; height: number } => {
    const targetAspectRatio = targetWidth / targetHeight;
    const imageAspectRatio = image.width / image.height;

    let cropWidth, cropHeight, cropX, cropY;

    if (targetAspectRatio > imageAspectRatio) {
        // Target is wider than image, crop height
        cropWidth = image.width;
        cropHeight = image.width / targetAspectRatio;
        cropX = 0;
        cropY = (image.height - cropHeight) / 2;
    } else {
        // Target is taller than image, crop width
        cropWidth = image.height * targetAspectRatio;
        cropHeight = image.height;
        cropX = (image.width - cropWidth) / 2;
        cropY = 0;
    }

    return { x: cropX, y: cropY, width: cropWidth, height: cropHeight };
};

/**
 * Gets face-aware crop area for an image using [x1, y1, x2, y2] format
 */
const getFaceAwareCropArea = (
    image: MediaImage,
    face: [number, number, number, number],
    targetWidth: number,
    targetHeight: number
): { x: number; y: number; width: number; height: number } => {
    const [x1, y1, x2, y2] = face;
    const targetAspectRatio = targetWidth / targetHeight;

    // Calculate crop dimensions
    let cropWidth, cropHeight;

    if (targetAspectRatio > image.width / image.height) {
        cropWidth = image.width;
        cropHeight = image.width / targetAspectRatio;
    } else {
        cropWidth = image.height * targetAspectRatio;
        cropHeight = image.height;
    }

    // Position crop to include the face
    const faceCenterX = (x1 + x2) / 2;
    const faceCenterY = (y1 + y2) / 2;

    let cropX = faceCenterX - cropWidth / 2;
    let cropY = faceCenterY - cropHeight / 2;

    // Ensure crop stays within image bounds
    if (cropX < 0) cropX = 0;
    if (cropY < 0) cropY = 0;
    if (cropX + cropWidth > image.width) cropX = image.width - cropWidth;
    if (cropY + cropHeight > image.height) cropY = image.height - cropHeight;

    return { x: cropX, y: cropY, width: cropWidth, height: cropHeight };
};

export function getMediaSize(
    media: Media,
    options: { width?: number; height?: number; noPix?: boolean } = {}
): MediaImage | null {
    if (!media) {
        return null;
    }

    const pix = options.noPix === true ? 1 : window.devicePixelRatio || 1;
    const getByWidth = (width: number): MediaImage | null => {
        if (media?.images?.length === 0) return null;
        const ret: MediaImage =
            media.images
                .sort((a, b) => (a.width > b.width ? 1 : -1))
                .find(image => image.width > width * pix) ?? media.images[media.images.length - 1];
        if (ret) {
            if (ret.width > width * pix * 2) {
                const w = Math.round(width * pix);
                const h = Math.round(width * pix * (ret.height / ret.width));
                return {
                    url: ret.url + `?w=${w}&h=${h}`,
                    width: w,
                    height: h
                };
            }
        }
        return ret;
    };

    const getByHeight = (height: number): MediaImage | null => {
        if (media?.images?.length === 0) return null;
        const ret: MediaImage =
            media.images
                .sort((a, b) => (a.height > b.height ? 1 : -1))
                .find(image => image.height > height * pix) ?? media.images[0];
        if (ret) {
            if (ret.height > height * pix * 2) {
                const w = Math.round(height * pix * (ret.width / ret.height));
                const h = Math.round(height * pix);
                return {
                    url: ret.url + `?w=${w}&h=${h}`,
                    width: w,
                    height: h
                };
            }
        }
        return ret;
    };

    // determine to use width or height to get the size
    let returnMedia: MediaImage | null = null;
    if (options.width !== undefined && options.height !== undefined) {
        if (media.images.length === 0) return null;
        if (options.width / options.height > media.images[0].width / media.images[0].height) {
            returnMedia = getByWidth(options.width);
        } else {
            returnMedia = getByHeight(options.height);
        }
    } else if (options.width !== undefined) {
        returnMedia = getByWidth(options.width);
    } else if (options.height !== undefined) {
        returnMedia = getByHeight(options.height);
    }

    return returnMedia;
}

export function getMediaImageUrl(
    media: MediaImage,
    options: { width?: number; height?: number; quality?: number } = {}
): string {
    if (!media) {
        return '';
    }

    let url = media.url;

    // Add optional parameters
    const params: string[] = [];
    if (options.width) params.push(`w=${options.width}`);
    if (options.height) params.push(`h=${options.height}`);
    if (options.quality) params.push(`q=${options.quality}`);

    if (params.length > 0) {
        url += `?${params.join('&')}`;
    }

    return url;
}
